import type { ReactElement, ReactNode } from 'react';

import { Icon } from '@/ui/components/Icon';
import { LanguageSelector } from '@/ui/i18n/LanguageSelector';
import { ThemeToggle, type ThemeToggleLabels } from '@/ui/theme/ThemeToggle';

import styles from './Header.module.css';

/**
 * The top bar — ADR-0101 §7.4, docs/DESIGN.md §4.2 (`--bar-top`: 56px, 64 from 960), §6.2.
 *
 * Logo tile · wordmark · divider · privacy claim · status slot · language · theme. It is 56px
 * and not merged into the toolbar for the reason DESIGN §4.2 gives: merging saves a row and
 * costs the primary action its own, putting Format in line with a wordmark and two settings
 * controls. Under ADR-0101 they are not even on the same surface — the header sits on the
 * room's aurora, and the toolbar is the phone's bottom dock or the card's first row.
 *
 * ═══ THE PRIVACY LINE IS NOT DECORATION ═══
 *
 * "Runs locally · nothing uploaded" is rendered as a plain sentence in the chrome because it
 * is a claim the ARCHITECTURE actually enforces — `connect-src 'none'` in nginx.conf
 * (ADR-0023) means the page CANNOT upload the document; there is no endpoint, no telemetry
 * and no analytics anywhere in the build. Most products that print this sentence are asking
 * to be believed. This one is describing a header that a browser enforces. It is the first
 * thing a person pasting a production payload wants to know, so it goes at the top — and it
 * is still the first thing the layout drops below 600px, because below that width the
 * document needs the pixels more.
 *
 * The lock beside it is the one icon in this file, and it is `aria-hidden` like every other:
 * docs/DESIGN.md §7.5 — no meaning is carried by an icon alone, and the sentence carries all
 * of it. The glyph is there so the claim is findable at a glance, not so it can replace words.
 *
 * ═══ THE WORDMARK IS AN <h1>, NOT A LINK ═══
 *
 * The mockup draws `<a href="#" class="wordmark">`. There is nowhere to go: v1 is one screen
 * with no router (recon §1.1), and a link to `/` would RELOAD THE PAGE and silently discard
 * whatever the user had in the buffer — the document lives in memory and is never persisted
 * (ADR-0023). So it is the page's heading instead, which is what it actually is. DESIGN
 * beats the mockup where they disagree, and this is one of the two places they do.
 *
 * ═══ AND THE TILE IS OUTSIDE IT ═══
 *
 * `{}` is drawn by a `<span>` that PRECEDES the `<h1>` rather than by a first child of it, so
 * the heading's text content stays exactly `json/formatter`. A decorative pair of braces
 * inside the heading would reach `getByRole('heading')`, the document outline and every
 * accessible name computed from it, to say nothing of a screen reader announcing two more
 * punctuation marks on a page whose subject is punctuation.
 */

export interface HeaderProps {
  /** Localised, `meta.productName` — e.g. `json/formatter`. */
  readonly productName: string;
  /** Localised, `meta.privacy`. */
  readonly privacyLabel: string;
  /** Localised labels for the theme control; it names the state it will MOVE TO. */
  readonly themeLabels: ThemeToggleLabels;
  /** The lint status pill. A slot, because the shell knows nothing about JSON. */
  readonly status?: ReactNode;
  /** Anything the shell adds to the right of the theme toggle (help, source). */
  readonly actions?: ReactNode;
}

/**
 * `json/formatter` renders as `json` + an accented `/` + a lighter `formatter`, which is the
 * whole of the brand (docs/DESIGN.md §3.2 — mono, because the product name is a path).
 *
 * The split is computed from the string rather than hardcoded because the string is
 * LOCALISED: a locale that ships a name without a slash gets one plain wordmark instead of a
 * mis-sliced one, and nothing here has to know which locale is in force.
 */
function Wordmark({ name }: { readonly name: string }): ReactElement {
  const slash = name.indexOf('/');
  if (slash < 0) return <>{name}</>;

  return (
    <>
      {name.slice(0, slash)}
      <em className={styles.slash}>/</em>
      <span className={styles.tail}>{name.slice(slash + 1)}</span>
    </>
  );
}

export function Header({
  productName,
  privacyLabel,
  themeLabels,
  status,
  actions,
}: HeaderProps): ReactElement {
  return (
    <header className={styles.bar}>
      <div className={styles.brand}>
        {/* Decorative: the braces are the mark, and the heading beside them is the name. */}
        <span className={styles.logo} aria-hidden="true">
          {'{}'}
        </span>

        <h1 className={styles.wordmark}>
          <Wordmark name={productName} />
        </h1>
      </div>

      <div className={styles.sep} aria-hidden="true" />

      <p className={styles.privacy}>
        <Icon name="lock" />
        {privacyLabel}
      </p>

      {/*
        One shrinkable group, pushed right by `margin-left: auto`. A flexible spacer element
        would let the door grow into the whole bar before the wordmark gave up a pixel.
      */}
      <div className={styles.end}>
        {status}
        <LanguageSelector />
        <ThemeToggle labels={themeLabels} />
        {actions}
      </div>
    </header>
  );
}

export default Header;
